<script lang="ts" setup>
import { app } from '@/composition/app'
import { GroupState } from '@/composition/groups'
import { TimeFrame } from '@/models/Leaderboard'
import { UIContentWrap } from '@gohighlevel/ghl-ui'
import { computed, onMounted, onUnmounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import InfoList from '@/components/InfoList.vue'
import UserProfileTooltip from '@/components/common/tooltip/UserProfileTooltip.vue'
import GroupInfo from '@/components/groups/GroupInfoIndex.vue'
import { redirectToNoCommunitiesPage } from '@/components/helper'
import LeaderboardTable from '@/components/leaderboard/web/LeaderboardTable.vue'
import Posts from '@/components/posts/Posts.vue'
import { useCommunitiesToastNotification } from '@/helper/communitiesToastNotification.helper'
import { NO_GROUP_SLUG } from '@/helper/const'
import { isGokollab } from '@ghl/clientportal-core/init'
import { useRoute, useRouter } from 'vue-router'

const router = useRouter()
const route = useRoute()
const isModalRoute = computed(() => route.meta?.openAsModal === true)
const { t } = useI18n()

function notificationAlert(title: string, content: string) {
  const notification = useCommunitiesToastNotification()
  notification.create({
    title: title,
    content: content,
    duration: 10000,
  })
}

onMounted(() => {
  if (GroupState.state?.noCommunities) {
    router.push({ name: 'no-communities-mobile' })
  }

  if (route.params.groupSlug === NO_GROUP_SLUG) {
    redirectToNoCommunitiesPage(router)
  }

  // Check if user was redirected from old slug and show notification
  const urlParams = new URLSearchParams(window.location.search)
  if (isGokollab() && urlParams.get('redirected_from_old_slug') === 'true') {
    try {
      const title = t('notifications.redirectedFromOldSlug.title')
      const content = t('notifications.redirectedFromOldSlug.content')

      notificationAlert(title, content)

      // Clean up the URL parameter after showing notification
      urlParams.delete('redirected_from_old_slug')
      const newUrl =
        window.location.pathname +
        (urlParams.toString() ? '?' + urlParams.toString() : '')
      window.history.replaceState({}, '', newUrl)
    } catch (error) {
      console.log('error:', error)
    }
  }

  window.scrollTo(0, 0)
  setTimeout(handleStickyScroll, 1000)
  window.addEventListener('scroll', handleScroll)
  window.addEventListener('scroll', handleStickyScroll)
})
const groupInfoRef = ref()
function handleStickyScroll() {
  const groupInfoElement = groupInfoRef.value
  if (groupInfoElement) {
    const rect = groupInfoElement.getBoundingClientRect()
    // positive values of top are not required
    GroupState.state.groupInfoTopPosition = Math.min(
      130,
      window.innerHeight - rect.bottom + rect.top - 10
    )
    groupInfoElement.style.top = `${GroupState.state.groupInfoTopPosition}px`
  } else {
    setTimeout(handleStickyScroll, 3000)
  }
}

onUnmounted(() => {
  window.removeEventListener('scroll', handleStickyScroll)
  window.removeEventListener('scroll', handleScroll)
})

const scrollData = reactive({
  heightOffset: 0,
  widthOffset: 0,
})

async function handleScroll() {
  // Calculate the scroll position relative to the page height
  scrollData.widthOffset = window.pageXOffset
  scrollData.heightOffset = window.pageYOffset
}
</script>
<template>
  <div
    v-if="!app.state.isMobile"
    id="container_group_discussion"
    class="grid grid-cols-6 justify-center gap-5"
  >
    <transition name="fade">
      <div id="post_list" class="sticky_scroll col-span-4">
        <UserProfileTooltip :scroll-data="scrollData" />
        <router-view
          v-if="route.params.postId && !isModalRoute && !app.state.isMobile"
        />
        <Posts v-else />
      </div>
    </transition>

    <transition name="fade">
      <div
        id="group_info"
        ref="groupInfoRef"
        class="sidebar sticky_scroll right col-span-2 h-fit pb-5"
      >
        <InfoList class="sidebar col-span-2">
          <template #info-details>
            <GroupInfo />
          </template>
        </InfoList>
        <LeaderboardTable
          :timeframe="TimeFrame.THIRTY_DAY"
          :row-limit="5"
          :is-info-card="true"
          class="mt-5"
        />
      </div>
    </transition>
  </div>
  <UIContentWrap
    v-else
    class="outer-container full-screen lg:mobile-padding bg-communities-surface pb-2 lg:px-3"
  >
    <div
      class="mobile-content-container relative grid-cols-1 gap-4 lg:grid lg:grid-cols-10 xl:grid-cols-36 xl:gap-5"
    >
      <transition name="fade">
        <InfoList
          class="md:col-span-17 md:block md:max-h-fit lg:hidden xl:hidden"
        >
          <template #info-details>
            <GroupInfo />
          </template>
        </InfoList>
      </transition>
      <transition name="fade">
        <Posts class="md:col-span-17 lg:col-span-8 xl:col-span-20" />
      </transition>
    </div>
  </UIContentWrap>
</template>
<style scoped>
.sticky_scroll {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: sticky;
}

html,
body {
  position: relative;
  margin: 0;
  border: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}
body {
  overflow: auto;
}
.group__info {
  width: 300px;
}
/* .posts {
  min-width: 555px;
  max-width: 760px;
}*/

.mobile-content-container {
  height: calc(100vh - 46.8px);
}

.sidebar {
  min-width: 250px;
}

@media (max-width: 930px) {
  .outer-container {
    padding: 0px !important;
    padding-bottom: 75px !important;
  }
}
.hl-wrapper-container {
  padding: 0px !important;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s ease-in-out;
}
.fade-enter, .fade-leave-to /* .fade-leave-active in <2.1.8 */ {
  opacity: 0;
}
</style>
